import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { setObservabilityUser } from '../observability';
import { identifyAnalyticsUser } from '../analytics';
import axios from 'axios';
import { getRedirectResult, onAuthStateChanged } from 'firebase/auth';
import { API_URL } from '../utils/api';
import { sessionApi } from '../utils/apiClient';
import { getFirebaseAuth } from '../utils/firebase';
import { LEGAL_VERSIONS } from '../pages/LegalPage';
import ConsentGate from '../components/ConsentGate';

export interface Customer {
  id: string;
  email: string | null;
  phone: string | null;
  name: string | null;
  username: string | null;
  totalOrders: number;
  totalSpent: number;
  loyaltyPoints: number;
}

export interface BadgePayload {
  id: string;
  badgeId: string;
  visitCount: number;
  currentTierId: string | null;
  currentTierName: string | null;
  currentTierImage: string | null;
  badgeName: string;
  badgeIcon: string | null;
  restaurantName: string | null;
  unlockedAt: string;
}

interface CustomerAuthContextType {
  customer: Customer | null;
  token: string | null;
  loginWithFirebaseToken: (idToken: string, orderId?: string) => Promise<{ customer: Customer; token: string; newBadges: BadgePayload[] }>;
  devLogin: (email: string) => Promise<{ customer: Customer; token: string }>;
  updateCustomer: (updates: Partial<Customer>) => void;
  logout: () => void;
  loading: boolean;
  isAuthenticated: boolean;
}

const CustomerAuthContext = createContext<CustomerAuthContextType | undefined>(undefined);

export const useCustomerAuth = () => {
  const context = useContext(CustomerAuthContext);
  if (!context) {
    throw new Error('useCustomerAuth must be used within a CustomerAuthProvider');
  }
  return context;
};

const CUSTOMER_TOKEN_KEY = 'olso_customer_token';
const CUSTOMER_DATA_KEY = 'olso_customer_data';

// ── First-party cookie helpers ──
// These cookies are set on the FRONTEND domain via document.cookie.
// iOS shares first-party cookies between Safari and PWA (same origin).
// This is the ONLY reliable bridge for auth persistence across contexts.
const COOKIE_MAX_AGE = 60 * 60 * 24 * 30; // 30 days

function setAuthCookies(token: string, customer: Customer) {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `olso_jwt=${token}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax${secure}`;
  document.cookie = `olso_cust=${encodeURIComponent(JSON.stringify(customer))}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax${secure}`;
}

function clearAuthCookies() {
  document.cookie = 'olso_jwt=; path=/; max-age=0';
  document.cookie = 'olso_cust=; path=/; max-age=0';
  document.cookie = '__olso_pwa_idtoken=; path=/; max-age=0';
}

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
  return match ? match[1] : null;
}

function getAuthFromCookies(): { token: string; customer: Customer } | null {
  const jwt = getCookie('olso_jwt');
  const custRaw = getCookie('olso_cust');
  if (jwt && custRaw) {
    try {
      return { token: jwt, customer: JSON.parse(decodeURIComponent(custRaw)) };
    } catch { /* malformed cookie */ }
  }
  return null;
}

// ── localStorage helpers ──
function getStoredToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(CUSTOMER_TOKEN_KEY);
}

function getStoredCustomer(): Customer | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(CUSTOMER_DATA_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function getStoredAuth(): { customer: Customer; token: string } | null {
  // Try localStorage first, then cookies
  const c = getStoredCustomer();
  const t = getStoredToken();
  if (c && t) return { customer: c, token: t };
  return getAuthFromCookies();
}

// Restore from all available sources: localStorage → cookies → backend session
function getInitialAuth(): { customer: Customer | null; token: string | null } {
  // 1. localStorage (fastest, works within same context)
  const lsToken = getStoredToken();
  const lsCustomer = getStoredCustomer();
  authDebug(`init: ls_token=${!!lsToken} ls_cust=${!!lsCustomer}`);
  if (lsToken && lsCustomer) { authDebug('init: using localStorage'); return { customer: lsCustomer, token: lsToken }; }

  // 2. First-party cookies (shared between Safari ↔ PWA on iOS)
  const cookieAuth = getAuthFromCookies();
  authDebug(`init: cookie_auth=${!!cookieAuth}`);
  if (cookieAuth) {
    authDebug('init: using cookies → promote to localStorage');
    localStorage.setItem(CUSTOMER_TOKEN_KEY, cookieAuth.token);
    localStorage.setItem(CUSTOMER_DATA_KEY, JSON.stringify(cookieAuth.customer));
    return cookieAuth;
  }

  authDebug('init: no auth found');
  return { customer: null, token: null };
}

// ── Manifest + PWA auth token strategy ──
// iOS 17+ isolates ALL storage between Safari and PWA.
// The ONLY bridge is the URL itself. But iOS strips query params AND
// extra path segments from the manifest start_url.
//
// Strategy: embed the auth token in the qrCode path segment using ~ delimiter:
//   /order/QRCODE~CTTOKEN
// This is a single path segment that iOS won't strip.
// On PWA launch, OrderStatus splits on ~ to extract the token.
//
// CRITICAL: Do NOT inject manifest at module load (before _ct is known).
// Only inject AFTER the token is embedded, so iOS reads the correct start_url.

const MANIFEST_BASE = {
  name: 'OLSO',
  short_name: 'OLSO',
  description: 'Order tracking & Food Passport',
  display: 'standalone' as const,
  scope: '/',
  background_color: '#FAF7F2',
  theme_color: '#FAF7F2',
  icons: [
    { src: '/icon-192.png', sizes: '192x192', type: 'image/png' },
    { src: '/icon-512.png', sizes: '512x512', type: 'image/png' },
  ],
};

function setManifest(startUrl: string) {
  try {
    const manifest = { ...MANIFEST_BASE, start_url: startUrl };
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(manifest));
    let link = document.querySelector('link[rel="manifest"]') as HTMLLinkElement | null;
    if (link) {
      link.href = dataUri;
    } else {
      link = document.createElement('link');
      link.rel = 'manifest';
      link.href = dataUri;
      document.head.appendChild(link);
    }
    authDebug(`manifest: start_url=${startUrl.slice(0, 80)}`);
  } catch (err) {
    authDebug(`manifest failed: ${err}`);
  }
}

// Extract qrCode from current path (handles both /order/CODE and /order/CODE~TOKEN)
function getCurrentQrCode(): string | null {
  const match = window.location.pathname.match(/^\/order\/([^/~]+)/);
  return match ? match[1] : null;
}

// Fetch a persistent token and embed it in the URL + manifest for PWA.
// URL format: /order/QRCODE~CTTOKEN (single path segment, survives iOS)
async function embedPersistentTokenInUrl(jwtToken: string) {
  try {
    // Already has token embedded (via ~ in path or ?_ct in query)
    const currentUrl = new URL(window.location.href);
    const pathHasToken = window.location.pathname.includes('~');
    const queryHasToken = currentUrl.searchParams.has('_ct');

    if (pathHasToken) {
      // Already embedded in path — just set manifest
      setManifest(window.location.pathname);
      return;
    }
    if (queryHasToken) {
      // Has query param — build path version for manifest
      const qr = getCurrentQrCode();
      const ct = currentUrl.searchParams.get('_ct');
      if (qr && ct) setManifest(`/order/${qr}~${ct}`);
      return;
    }

    const base = API_URL || '';
    const { data } = await axios.post(`${base}/api/auth/customer/persistent-token`, null, {
      headers: { Authorization: `Bearer ${jwtToken}` },
    });
    if (data.persistentToken) {
      const qr = getCurrentQrCode();
      if (qr) {
        // Update browser URL with ?_ct for restoreFromPersistentToken
        const url = new URL(window.location.href);
        url.searchParams.set('_ct', data.persistentToken);
        window.history.replaceState(null, '', url.toString());
        // Set manifest with ~TOKEN path (survives iOS)
        setManifest(`/order/${qr}~${data.persistentToken}`);
      }
    }
  } catch (err) {
    console.warn('[auth] Failed to embed persistent token:', err);
  }
}

// Debug helper — visible on iPhone where there's no DevTools
// Set to true to re-enable debug logging to window.__authLog
const AUTH_DEBUG = false;
function authDebug(msg: string) {
  if (!AUTH_DEBUG) return;
  console.log(`[auth] ${msg}`);
  if (typeof window !== 'undefined') {
    (window as unknown as Record<string, string[]>).__authLog =
      (window as unknown as Record<string, string[]>).__authLog || [];
    (window as unknown as Record<string, string[]>).__authLog.push(`${new Date().toISOString().slice(11,19)} ${msg}`);
  }
}

// Exchange _ct URL param (or ~TOKEN in path) for fresh auth on PWA launch
async function restoreFromPersistentToken(): Promise<{ customer: Customer; token: string } | null> {
  const url = new URL(window.location.href);
  // Check query param first, then ~ in path (iOS PWA launch)
  let ct = url.searchParams.get('_ct');
  if (!ct) {
    const pathMatch = window.location.pathname.match(/~([A-Za-z0-9]+)$/);
    if (pathMatch) {
      ct = pathMatch[1];
      authDebug(`restore: found token in path via ~`);
      // Rewrite URL to clean format (remove ~ from visible URL)
      const cleanPath = window.location.pathname.replace(/~[A-Za-z0-9]+$/, '');
      const cleanUrl = new URL(window.location.href);
      cleanUrl.pathname = cleanPath;
      cleanUrl.searchParams.set('_ct', ct);
      window.history.replaceState(null, '', cleanUrl.toString());
    }
  }
  authDebug(`restore: _ct=${ct ? ct.slice(0, 8) + '…' : 'null'}, standalone=${window.matchMedia('(display-mode: standalone)').matches}`);
  if (!ct) return null;
  try {
    const base = API_URL || '';
    authDebug(`restore: calling GET /restore`);
    const { data } = await axios.get(`${base}/api/auth/customer/restore`, { params: { _ct: ct } });
    authDebug(`restore: OK, customer=${data.customer?.id?.slice(0, 8)}`);
    return { customer: data.customer, token: data.token };
  } catch (err: unknown) {
    // Only remove _ct if server explicitly rejected it (401) — not on network errors
    const status = (err as { response?: { status?: number } })?.response?.status;
    authDebug(`restore: FAIL status=${status}`);
    if (status === 401) {
      url.searchParams.delete('_ct');
      window.history.replaceState(null, '', url.toString());
    }
    return null;
  }
}

interface CustomerAuthProviderProps {
  children: ReactNode;
}

async function checkConsent(t: string): Promise<boolean> {
  try {
    const base = API_URL || '';
    const authHeader = { Authorization: `Bearer ${t}` };
    const { data } = await axios.get(`${base}/api/customers/consent`, { headers: authHeader });
    const byType: Record<string, { version: string }> = {};
    for (const c of (data.consents ?? [])) byType[c.type] = c;
    return (
      byType['OFFER_TERMS']?.version !== LEGAL_VERSIONS.offer ||
      byType['PRIVACY']?.version !== LEGAL_VERSIONS.privacy
    );
  } catch {
    return false; // on failure, do not block the user
  }
}

export const CustomerAuthProvider = ({ children }: CustomerAuthProviderProps) => {
  const initial = getInitialAuth();
  const [customer, setCustomer] = useState<Customer | null>(initial.customer);
  const [token, setToken] = useState<string | null>(initial.token);
  const [loading, setLoading] = useState(!initial.token); // skip loading if already have auth
  const [needsConsent, setNeedsConsent] = useState(false);

  // Pre-warm Firebase SDK at provider mount so the auth modal renders
  // OAuth buttons instantly when opened (instead of waiting for SDK init).
  useEffect(() => {
    getFirebaseAuth().catch(() => {});
  }, []);

  // Check for PWA auth handoff token (set by auth-helper.html via cookie)
  const checkPwaAuthCookie = async () => {
    const idToken = getCookie('__olso_pwa_idtoken');
    if (!idToken) return false;
    // Clear the handoff cookie immediately
    document.cookie = '__olso_pwa_idtoken=; path=/; max-age=0';
    try {
      const base = API_URL || '';
      const { data } = await axios.post(`${base}/api/auth/customer/verify`, { idToken });
      const { customer: c, token: t } = data;
      localStorage.setItem(CUSTOMER_TOKEN_KEY, t);
      localStorage.setItem(CUSTOMER_DATA_KEY, JSON.stringify(c));
      setAuthCookies(t, c);
      setCustomer(c);
      setToken(t);
      checkConsent(t).then(needs => setNeedsConsent(needs));
      return true;
    } catch {
      return false;
    }
  };

  const trySessionRestore = (onDone?: () => void) => {
    sessionApi.restore<{ customer: Customer; token: string }>()
      .then(({ data }) => {
        localStorage.setItem(CUSTOMER_TOKEN_KEY, data.token);
        localStorage.setItem(CUSTOMER_DATA_KEY, JSON.stringify(data.customer));
        setAuthCookies(data.token, data.customer);
        setCustomer(data.customer);
        setToken(data.token);
        checkConsent(data.token).then(needs => setNeedsConsent(needs));
      })
      .catch(() => {})
      .finally(() => { onDone?.(); });
  };

  useEffect(() => {
    authDebug(`useEffect: token=${!!token}`);
    if (token) {
      // Already authenticated — ensure _ct is in URL for PWA persistence
      authDebug('useEffect: already auth, embedding _ct');
      embedPersistentTokenInUrl(token);
      checkConsent(token).then(needs => setNeedsConsent(needs));
      setLoading(false);
      return;
    }
    // Check Firebase redirect result (iOS signInWithRedirect flow)
    // On iOS, signInWithRedirect opens Safari. After auth, Firebase redirects
    // back to our URL — but in SAFARI, not the PWA. So this code runs in Safari.
    // We log in locally (for Safari) AND post the token to the relay so the PWA
    // can pick it up when it regains focus.
    const checkRedirectResult = async (): Promise<boolean> => {
      const redirectInfoStr = localStorage.getItem('olso_auth_redirect');
      if (!redirectInfoStr) return false;
      authDebug('redirect: found pending redirect info');

      const firebaseAuth = await getFirebaseAuth();
      if (!firebaseAuth) return false;

      const info = JSON.parse(redirectInfoStr);

      // Helper: complete auth and post to relay for PWA
      const completeRedirectAuth = async (idToken: string): Promise<boolean> => {
        try {
          // Post to relay so the PWA standalone instance can pick it up
          if (info.relayState) {
            authDebug('redirect: posting token to relay');
            fetch(`${API_URL || ''}/api/auth/customer/pwa-handoff`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ state: info.relayState, idToken }),
            }).catch(() => {}); // fire-and-forget
          }
          localStorage.removeItem('olso_auth_redirect');
          await loginWithFirebaseToken(idToken, info.orderId);
          return true;
        } catch (err) {
          authDebug(`redirect: login failed: ${err}`);
          return false;
        }
      };

      // 1. Check currentUser (cached from previous redirect)
      if (firebaseAuth.currentUser) {
        authDebug('redirect: found currentUser');
        const idToken = await firebaseAuth.currentUser.getIdToken();
        return completeRedirectAuth(idToken);
      }

      // 2. Try getRedirectResult
      try {
        const result = await getRedirectResult(firebaseAuth);
        if (result) {
          authDebug('redirect: getRedirectResult succeeded');
          const idToken = await result.user.getIdToken();
          return completeRedirectAuth(idToken);
        }
      } catch (err) {
        authDebug(`redirect: getRedirectResult failed: ${err}`);
      }

      // 3. Fallback: onAuthStateChanged (waits up to 5s)
      authDebug('redirect: falling back to onAuthStateChanged');
      return new Promise<boolean>((resolve) => {
        const timeout = setTimeout(() => {
          authDebug('redirect: onAuthStateChanged timed out');
          unsubscribe();
          resolve(false);
        }, 5000);

        const unsubscribe = onAuthStateChanged(firebaseAuth, async (user) => {
          clearTimeout(timeout);
          unsubscribe();
          if (user) {
            authDebug('redirect: onAuthStateChanged got user');
            const idToken = await user.getIdToken();
            resolve(await completeRedirectAuth(idToken));
          } else {
            resolve(false);
          }
        });
      });
    };

    // Use async IIFE for clean sequential flow
    (async () => {
      // 1. Check Firebase redirect FIRST (page reloaded after signInWithRedirect on iOS)
      const redirectOk = await checkRedirectResult();
      if (redirectOk) { setLoading(false); return; }

      // 1b. Check if Safari already posted the redirect token to relay
      try {
        const redirectInfoStr = localStorage.getItem('olso_auth_redirect');
        if (redirectInfoStr) {
          const info = JSON.parse(redirectInfoStr);
          if (info.relayState) {
            authDebug('init: checking redirect relay');
            const res = await fetch(`${API_URL || ''}/api/auth/customer/pwa-handoff?state=${encodeURIComponent(info.relayState)}`);
            if (res.ok) {
              const data = await res.json();
              if (data.idToken) {
                localStorage.removeItem('olso_auth_redirect');
                await loginWithFirebaseToken(data.idToken, info.orderId);
                authDebug('init: redirect relay auth OK');
                setLoading(false);
                return;
              }
            }
          }
        }
      } catch { /* continue */ }

      // 2. Try persistent token from URL (seamless PWA launch)
      authDebug('useEffect: trying restoreFromPersistentToken');
      const result = await restoreFromPersistentToken();
      if (result) {
        authDebug('useEffect: restore OK!');
        localStorage.setItem(CUSTOMER_TOKEN_KEY, result.token);
        localStorage.setItem(CUSTOMER_DATA_KEY, JSON.stringify(result.customer));
        setAuthCookies(result.token, result.customer);
        setCustomer(result.customer);
        setToken(result.token);
        embedPersistentTokenInUrl(result.token);
        setLoading(false);
        return;
      }

      // 3. Try pending auth relay state (PWA reloaded during auth flow)
      const pendingState = sessionStorage.getItem('olso_auth_state');
      if (pendingState) {
        authDebug('useEffect: found pending auth relay state');
        try {
          const relayRes = await fetch(`${API_URL || ''}/api/auth/customer/pwa-handoff?state=${encodeURIComponent(pendingState)}`);
          if (relayRes.ok) {
            const relayData = await relayRes.json();
            if (relayData.idToken) {
              sessionStorage.removeItem('olso_auth_state');
              await loginWithFirebaseToken(relayData.idToken);
              authDebug('useEffect: relay auth OK');
              setLoading(false);
              return;
            }
          }
        } catch { /* continue */ }
      }

      // 4. Try PWA handoff cookie, then backend session cookie
      authDebug('useEffect: restore failed, trying pwa cookie');
      const cookieOk = await checkPwaAuthCookie();
      if (cookieOk) {
        authDebug('useEffect: pwa cookie OK');
        setLoading(false);
      } else {
        authDebug('useEffect: trying session restore');
        trySessionRestore(() => setLoading(false));
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-check auth when app regains focus (user returns from Safari)
  useEffect(() => {
    const handleVisibility = async () => {
      if (document.visibilityState !== 'visible') return;
      if (getStoredToken()) return; // already logged in

      // Check for redirect relay (Safari completed auth, posted token to relay)
      try {
        const redirectInfoStr = localStorage.getItem('olso_auth_redirect');
        if (redirectInfoStr) {
          const info = JSON.parse(redirectInfoStr);
          if (info.relayState) {
            authDebug('visibility: checking redirect relay');
            const res = await fetch(`${API_URL || ''}/api/auth/customer/pwa-handoff?state=${encodeURIComponent(info.relayState)}`);
            if (res.ok) {
              const data = await res.json();
              if (data.idToken) {
                localStorage.removeItem('olso_auth_redirect');
                await loginWithFirebaseToken(data.idToken, info.orderId);
                authDebug('visibility: redirect relay auth OK');
                return;
              }
            }
          }
        }
      } catch { /* continue */ }

      // Check for pending auth relay (legacy sessionStorage flow)
      try {
        const pendingState = sessionStorage.getItem('olso_auth_state');
        if (pendingState) {
          const res = await fetch(`${API_URL || ''}/api/auth/customer/pwa-handoff?state=${encodeURIComponent(pendingState)}`);
          if (res.ok) {
            const data = await res.json();
            if (data.idToken) {
              sessionStorage.removeItem('olso_auth_state');
              await loginWithFirebaseToken(data.idToken);
              authDebug('visibility: relay auth OK');
              return;
            }
          }
        }
      } catch { /* continue to other methods */ }

      // Check for PWA auth handoff cookie
      const restored = await checkPwaAuthCookie();
      if (restored) return;

      // Check first-party auth cookies (may have been set in Safari)
      const cookieAuth = getAuthFromCookies();
      if (cookieAuth) {
        localStorage.setItem(CUSTOMER_TOKEN_KEY, cookieAuth.token);
        localStorage.setItem(CUSTOMER_DATA_KEY, JSON.stringify(cookieAuth.customer));
        setCustomer(cookieAuth.customer);
        setToken(cookieAuth.token);
        return;
      }

      // Last resort: backend session cookie
      trySessionRestore();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loginWithFirebaseToken = async (idToken: string, orderId?: string) => {
    // base = '' on localhost (Vite proxy routes /api/* to backend); set API URL on prod
    const base = API_URL || '';
    const { data } = await axios.post(`${base}/api/auth/customer/verify`, { idToken, orderId });
    const { customer: c, token: t, newBadges = [] } = data;

    // Persist in localStorage + first-party cookies + backend session cookie
    localStorage.setItem(CUSTOMER_TOKEN_KEY, t);
    localStorage.setItem(CUSTOMER_DATA_KEY, JSON.stringify(c));
    setAuthCookies(t, c);
    setCustomer(c);
    setToken(t);

    // Backend session cookie (cross-origin, fire-and-forget)
    sessionApi.create(idToken).catch(() => {});

    // Get persistent token and embed in URL for seamless PWA launch
    embedPersistentTokenInUrl(t);

    // Check if customer needs to accept current legal versions
    const needs = await checkConsent(t);
    setNeedsConsent(needs);

    return { customer: c, token: t, newBadges };
  };

  const devLogin = async (email: string) => {
    const base = API_URL || '';
    const { data } = await axios.post(`${base}/api/auth/customer/dev-login`, { email });
    const { customer: c, token: t } = data;
    localStorage.setItem(CUSTOMER_TOKEN_KEY, t);
    localStorage.setItem(CUSTOMER_DATA_KEY, JSON.stringify(c));
    setAuthCookies(t, c);
    setCustomer(c);
    setToken(t);
    embedPersistentTokenInUrl(t);
    return { customer: c, token: t };
  };

  const logout = () => {
    setCustomer(null);
    setToken(null);
    localStorage.removeItem(CUSTOMER_TOKEN_KEY);
    localStorage.removeItem(CUSTOMER_DATA_KEY);
    clearAuthCookies();
    sessionApi.destroy().catch(() => {});
  };

  const updateCustomer = (updates: Partial<Customer>) => {
    setCustomer(prev => {
      if (!prev) return prev;
      const updated = { ...prev, ...updates };
      localStorage.setItem('olso_customer_data', JSON.stringify(updated));
      return updated;
    });
  };

  useEffect(() => {
    const id = customer?.id || null;
    setObservabilityUser(id);
    identifyAnalyticsUser(id);
  }, [customer?.id]);

  const value: CustomerAuthContextType = {
    customer,
    token,
    loginWithFirebaseToken,
    devLogin,
    updateCustomer,
    logout,
    loading,
    isAuthenticated: !!customer && !!token,
  };

  return (
    <CustomerAuthContext.Provider value={value}>
      {needsConsent && customer && token && (
        <ConsentGate
          onComplete={() => setNeedsConsent(false)}
          authHeader={{ Authorization: `Bearer ${token}` }}
        />
      )}
      {children}
    </CustomerAuthContext.Provider>
  );
};
